import React, { useState, useEffect, useRef } from 'react';
import {
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	TextField,
	Button,
	FormControl,
	InputLabel,
	MenuItem,
	Select,
	OutlinedInput,
	Chip,
	Box,
	SelectChangeEvent,
	IconButton,
	Grid,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import CloseIcon from '@mui/icons-material/Close';
import type { Category } from '../../types/cards';

const useStyles = makeStyles({
	inputField: {
		marginBottom: '15px',
	},
	imagePreview: {
		width: '100%',
		height: '200px',
		marginTop: '10px',
		objectFit: 'contain',
	},
	removeButton: {
		marginTop: '10px',
		color: '#CA0005',
		// '&:hover': {
		// 	color: 'white',
		// },
	},
});

interface CreateCategoriesProps {
	open: boolean;
	handleClose: () => void;
	handleSave: (category: Category) => void;
	category: Category | null;
	menuTypes: string[];
	loading: any;
}

const CreateCategories: React.FC<CreateCategoriesProps> = ({
	open,
	handleClose,
	handleSave,
	menuTypes,
	category,
	loading,
}) => {
	const classes = useStyles();
	const fileInputRef = useRef<HTMLInputElement>(null);
	console.log(menuTypes, 'menuTypes');

	const [name, setName] = useState(category?.name || '');
	const [description, setDescription] = useState(category?.description || '');
	const [slug, setSlug] = useState(category?.slug || '');
	const [image, setImage] = useState<File | null>(null);
	const [imagePreview, setImagePreview] = useState<string | null>(null);
	const [nameError, setNameError] = useState('');
	const [descriptionError, setDescriptionError] = useState('');
	const [imageError, setImageError] = useState('');
	const [menuType, setMenuType] = useState<string[]>([]);
	const [menuTypeOptions, setMenuTypeOptions] = useState<string[]>(menuTypes || []);
	console.log(imageError);
	useEffect(() => {
		setMenuTypeOptions(menuTypes || []);
	}, [menuTypes]);

	useEffect(() => {
		if (category) {
			setName(category.name);
			setDescription(category.description);
			setSlug(category.slug || '');
			setImage(null);
			setImagePreview(
				typeof category?.categoryImage === 'string' ? category.categoryImage : null
			);

			if (category.tag) {
				if (Array.isArray(category.tag)) {
					setMenuType(category.tag);
				} else if (typeof category.tag === 'string') {
					try {
						const parsed = JSON.parse(category.tag);
						setMenuType(Array.isArray(parsed) ? parsed : [parsed]);
					} catch (e) {
						setMenuType([]);
					}
				}
			} else {
				setMenuType([]);
			}
		} else {
			setName('');
			setDescription('');
			setSlug('');
			setImage(null);
			setImagePreview(null);
			setMenuType([]);
		}
	}, [category]);

	const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (file && file.size <= 500 * 1024 && ['image/jpeg', 'image/png'].includes(file.type)) {
			setImage(file);
			setImagePreview(URL.createObjectURL(file));
			setImageError('');
		} else {
			alert('Image must be JPEG or PNG and less than 500KB');
			// setImageError('Image is required');
		}
	};

	const handleRemoveImage = () => {
		setImage(null);
		setImagePreview(null);
		if (fileInputRef.current) {
			fileInputRef.current.value = '';
		}
	};

	const handleMenuTypeChange = (event: SelectChangeEvent<typeof menuType>) => {
		setMenuType(event.target.value as string[]);
	};

	const validateForm = () => {
		let valid = true;
		if (!name) {
			setNameError('Name is required');
			valid = false;
		} else {
			setNameError('');
		}
		if (!description) {
			setDescriptionError('Description is required');
			valid = false;
		} else {
			setDescriptionError('');
		}
		// if (!image && !imagePreview) {
		// 	setImageError('Image is required');
		// 	valid = false;
		// }
		return valid;
	};

	const handleSubmit = (event: React.FormEvent) => {
		event.preventDefault();
		if (validateForm()) {
			const formData: Category = {
				_id: category?._id || '',
				name,
				description,
				categoryImage: image || (imagePreview as string) || '',
				createdAt: category?.createdAt || new Date().toISOString(),
				createdBy: category?.createdBy || 'system',
				slug: slug || name.toLowerCase().replace(/ /g, '-'),
				tag: menuType,
				status: category?.status || '',
			};
			handleSave(formData);
			// handleClose();
		}
	};

	const handleCancel = () => {
		handleClose();
	};

	return (
		<Dialog open={open} onClose={handleCancel}>
			<DialogTitle>
				{category ? 'Edit Category' : 'Add Category'}{' '}
				<IconButton
					aria-label="close"
					onClick={handleCancel}
					sx={{
						position: 'absolute',
						right: 8,
						top: 8,
						color: (theme) => theme.palette.grey[500],
					}}
				>
					<CloseIcon />
				</IconButton>
			</DialogTitle>
			<DialogContent>
				<TextField
					margin="dense"
					label="Category Name"
					fullWidth
					value={name}
					onChange={(e) => {
						setName(e.target.value);
						setSlug(e.target.value.toLowerCase().replace(/ /g, '-'));
					}}
					className={classes.inputField}
					size="small"
					error={!!nameError}
					helperText={nameError}
					required
				/>
				<TextField
					margin="dense"
					label="Description"
					fullWidth
					value={description}
					onChange={(e) => setDescription(e.target.value)}
					className={classes.inputField}
					size="small"
					error={!!descriptionError}
					helperText={descriptionError}
					required
				/>
				<TextField
					margin="dense"
					label="Slug"
					fullWidth
					value={slug}
					onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/ /g, '-'))}
					className={classes.inputField}
					size="small"
					// required
				/>

				<FormControl fullWidth size="small" className={classes.inputField}>
					<InputLabel id="menu-type-label">Tag</InputLabel>
					<Select
						labelId="menu-type-label"
						multiple
						value={menuType}
						onChange={handleMenuTypeChange}
						input={<OutlinedInput label="Tag" />}
						renderValue={(selected) => (
							<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
								{(selected as string[]).map((value) => (
									<Chip key={value} label={value} />
								))}
							</Box>
						)}
					>
						{menuTypeOptions.map((type) => (
							<MenuItem key={type} value={type}>
								{type}
							</MenuItem>
						))}
					</Select>
				</FormControl>
				<Grid container>
					<Grid item xs={12}>
						<Box>
							{imagePreview && (
								<Box sx={{ mt: 2, position: 'relative', display: 'inline-block' }}>
									<img
										src={imagePreview}
										alt="Preview"
										style={{
											maxWidth: '100%',
											maxHeight: 100,
											position: 'relative',
										}}
									/>
									<IconButton
										onClick={handleRemoveImage}
										sx={{
											position: 'absolute',
											top: 0,
											right: 0,
											backgroundColor: 'rgba(255, 255, 255, 0.7)',
											'&:hover': {
												backgroundColor: 'rgba(255, 255, 255, 0.9)',
											},
										}}
									>
										<CloseIcon color="error" />
									</IconButton>
								</Box>
							)}
						</Box>
						<Button variant="contained" component="label" sx={{ mt: 2 }}>
							Upload Image
							<input
								type="file"
								hidden
								accept="image/*"
								className={classes.inputField}
								onChange={handleImageChange}
							/>
						</Button>
					</Grid>
				</Grid>
			</DialogContent>
			<DialogActions>
				<Button
					variant="outlined"
					onClick={handleCancel}
					sx={{
						color: 'rgb(202, 0, 5)',
						// '&:hover': {
						// 	color: 'white',
						// },
					}}
				>
					Cancel
				</Button>
				<Button
					onClick={handleSubmit}
					variant="contained"
					style={{ background: '#CA0005' }}
					loading={loading}
					loadingIndicator={'Loading...'}
				>
					Save
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default CreateCategories;
